import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { ReleaseNotesService, ReleaseNotesFormData } from "../../services/release-notes.service";
import { Button, TextInput, DatePicker } from "../../components";
import RichTextEditor from "../EmailTemplates/RichTextEditor";
import { ToastVariants } from "../../models";

const ReleaseNotesUpdate = () => {
  const toast = useContext(ToastContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorInitiated, setErrorInitiated] = useState(false);

  const [formData, setFormData] = useState<ReleaseNotesFormData>({
    releaseVersion: "",
    subReleaseVersion: "",
    tagline: "",
    date: "",
    appNotes: "",
  });

  const handleFormChange = (field: keyof ReleaseNotesFormData, value: string) => {
    // Filter input based on field type to prevent invalid characters
    let filteredValue = value;

    if (field === "releaseVersion") {
      // Only allow numbers and dots
      filteredValue = value.replace(/[^0-9.]/g, "");
      // Prevent consecutive dots
      filteredValue = filteredValue.replace(/\.{2,}/g, ".");
    } else if (field === "subReleaseVersion") {
      // Only allow numbers and dots
      filteredValue = value.replace(/[^0-9.]/g, "");
      // Prevent consecutive dots
      filteredValue = filteredValue.replace(/\.{2,}/g, ".");
    } else if (field === "tagline") {
      // Only allow alphanumeric characters and spaces
      filteredValue = value.replace(/[^a-zA-Z0-9 ]/g, "");
    }

    setFormData(prev => ({ ...prev, [field]: filteredValue }));
  };

  // Validation helper functions
  const validateReleaseVersion = (value: string): boolean => {
    // Only numerical values and dot (.) allowed
    const pattern = /^[0-9.]+$/;
    return pattern.test(value);
  };

  const validateSubReleaseVersion = (value: string): boolean => {
    // Only numerical values and dot (.) allowed
    const pattern = /^[0-9.]+$/;
    return pattern.test(value);
  };

  const validateTagline = (value: string): boolean => {
    // Only alphanumeric characters and spaces allowed
    const pattern = /^[a-zA-Z0-9 ]+$/;
    return pattern.test(value);
  };

  const validateDateFormat = (value: string): boolean => {
    // Format must be DD/MM/YYYY
    // DatePicker returns date in YYYY-MM-DD format, we need to check if it's valid
    if (!value) return false;

    // The DatePicker component returns YYYY-MM-DD format
    // We just need to ensure a date is selected
    return value.trim().length > 0;
  };

  const handleReleaseFormSubmit = async () => {
    setErrorInitiated(true);

    // Validate required fields
    if (!formData.releaseVersion.trim()) {
      toast?.openToast("Please provide release version", 2000, ToastVariants.Error);
      return;
    }

    if (!validateReleaseVersion(formData.releaseVersion)) {
      toast?.openToast("Release version should contain only numbers", 2000, ToastVariants.Error);
      return;
    }

    if (!formData.subReleaseVersion?.trim()) {
      toast?.openToast("Please provide sub release version", 2000, ToastVariants.Error);
      return;
    }

    if (!validateSubReleaseVersion(formData.subReleaseVersion)) {
      toast?.openToast("Sub release version should contain only numbers and dots", 2000, ToastVariants.Error);
      return;
    }

    if (formData.tagline && formData.tagline.trim() && !validateTagline(formData.tagline)) {
      toast?.openToast("Tagline should contain only letters, numbers and spaces", 2000, ToastVariants.Error);
      return;
    }

    if (!formData.date.trim()) {
      toast?.openToast("Please provide date", 2000, ToastVariants.Error);
      return;
    }

    if (!validateDateFormat(formData.date)) {
      toast?.openToast("Please provide a valid date in DD/MM/YYYY format", 2000, ToastVariants.Error);
      return;
    }

    if (!formData.appNotes.trim()) {
      toast?.openToast("Please provide app notes", 2000, ToastVariants.Error);
      return;
    }

    setLoading(true);
    try {
      const result = await ReleaseNotesService.submitReleaseNotes(formData);

      if (result.success) {
        toast?.openToast("Release form submitted successfully", 3000, ToastVariants.Success);
        // Navigate back to list page after successful submission
        navigate("/version_info");
      } else {
        toast?.openToast(result.message || "Failed to submit release form. Please try again.", 2000, ToastVariants.Error);
      }
    } catch (error: any) {
      toast?.openToast(error.message || "An error occurred. Please try again.", 2000, ToastVariants.Error);
    } finally {
      setLoading(false);
      setErrorInitiated(false);
    }
  };

  return (
    <main>
      <div className="container mx-auto p-8">
        <div className="mb-8 flex items-center gap-4">
          <button
            onClick={() => navigate("/version_info")}
            className="text-docsigna-purple-dark hover:text-docsigna-purple font-medium text-lg flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h2 className="text-2xl font-bold text-black">Create New Release Notes</h2>
        </div>

        <div className="max-w-4xl">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <TextInput
                label="Release Version"
                value={formData.releaseVersion}
                onChangeFunc={e => handleFormChange("releaseVersion", e.target.value)}
                placeholder="Enter release version"
                isError={errorInitiated && (!formData.releaseVersion.trim() || !validateReleaseVersion(formData.releaseVersion))}
                errorMsg={!formData.releaseVersion.trim() ? "Please provide release version" : "Release version should contain only numbers and dots"}
                isRequired
              />
            </div>
            <div>
              <TextInput
                label="Sub Release Version"
                value={formData.subReleaseVersion || ""}
                onChangeFunc={e => handleFormChange("subReleaseVersion", e.target.value)}
                placeholder="Enter sub release version"
                isError={
                  errorInitiated &&
                  (!formData.subReleaseVersion?.trim() ||
                    (!!formData.subReleaseVersion?.trim() && !validateSubReleaseVersion(formData.subReleaseVersion)))
                }
                errorMsg={
                  !formData.subReleaseVersion?.trim()
                    ? "Please provide sub release version"
                    : "Sub release version should contain only numbers and dots"
                }
                isRequired
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <TextInput
                label="Tagline"
                value={formData.tagline || ""}
                onChangeFunc={e => handleFormChange("tagline", e.target.value)}
                placeholder="Enter tagline"
                isError={errorInitiated && !!formData.tagline && !!formData.tagline.trim() && !validateTagline(formData.tagline)}
                errorMsg="Tagline should contain only letters, numbers and spaces"
              />
            </div>
            <div className="relative z-10">
              <DatePicker
                name="releaseDate"
                label="Date"
                value={formData.date}
                onChange={date => handleFormChange("date", date)}
                placeholder="MM/DD/YYYY"
                isRequired
                isError={errorInitiated && !formData.date.trim()}
                errorMsg="Please provide date"
                isDob={false}
              />
            </div>
          </div>

          <div className="mb-4 relative z-0">
            <label className="text-sm text-docsigna-blue-dark font-medium block w-full max-w-full mb-1">App Notes</label>
            <RichTextEditor
              value={formData.appNotes}
              onChange={newValue => handleFormChange("appNotes", newValue)}
              errorInitiated={errorInitiated}
              errorMsg="Please provide app notes"
              showFullscreen={false}
            />
          </div>

          <div className="w-max">
            <Button
              text="Submit New Release"
              onClick={handleReleaseFormSubmit}
              disabled={loading}
              variant={loading ? "loading" : "Primary"}
              className="w-[300px]"
            />
          </div>
        </div>
      </div>
    </main>
  );
};

export default ReleaseNotesUpdate;
